import { getStep } from '../registry';
import { BannerVariant, GuideBannerConfig, OFF_LINE_STEP_NUMBER, TourStep } from '../types';
import { resolveUi } from './resolveUi';

/**
 * Can "Go Back" land on this step?
 *
 * A step that waits for a media modal to close cannot be returned to. Its whole state is "the
 * user has a ShareModal open", and re-entering it reproduces neither half of that: nothing
 * reopens the modal, and if something did, the modal covers the banner so the user could not
 * go back again. Either way the tour is stuck.
 *
 * Derived from `ui.onMediaClose` rather than a per-step flag, because the property belongs to
 * the mechanism, not to the individual step — a fourteenth media step gets this for free instead
 * of reintroducing the bug by forgetting to mark itself.
 */
export const isReenterable = (step?: TourStep): boolean => !step?.ui.onMediaClose;

/**
 * Off the main line — the paused steps, which the config already marks `stepNumber: -1` ("no
 * position of my own"). A pause is a detour, not progress, so Go Back should pass over it.
 *
 * Without this, "pause, skip ahead, go back" lands the user right back on the paused step: it has
 * no media modal, so the walk stops there, and no guide variant, so `stepHomePath` finds no page to
 * navigate to either. They end up standing in the habitat they skipped TO, reading a banner headed
 * with the section they left.
 */
export const isOffMainLine = (step?: TourStep): boolean => step?.analytics?.stepNumber === OFF_LINE_STEP_NUMBER;

/**
 * A step Go Back may land on: on the main line, not waiting for a media modal, and not a modal of
 * its own. The last of those keeps a tour's opening modal off the end of the walk — re-entering a
 * dismissed welcome screen is the same dead end as re-entering a closed media modal.
 */
const isLanding = (step?: TourStep): boolean => isReenterable(step) && !isOffMainLine(step) && !step?.ui.modal;

/** Walk back from the penultimate entry to the nearest one satisfying `accepts`. */
const walkBack = (tourId: string, stepHistory: string[], accepts: (step?: TourStep) => boolean): number => {
  let index = stepHistory.length - 2;
  while (index > 0 && !accepts(getStep(tourId, stepHistory[index]))) index -= 1;
  return index;
};

/**
 * How many entries "Go Back" should pop: one, plus any steps it must pass over.
 *
 * Returns 0 when there is nowhere to go. Note this walks the recorded history rather than a
 * hard-coded target, which matters at merge points — `O1` is reachable from `Home 1`, `Home 2`
 * and `paused-departure`, so a fixed "back goes to X" would be wrong for two of the three.
 *
 * Two passes. The first prefers a real, main-line landing. If it bottoms out on something that is
 * not one — the only way that happens is a history short enough to reach the opening modal — the
 * second falls back to the original rule, the nearest re-enterable step, paused or not.
 *
 * The fallback does NOT exclude modal steps, so on a history short enough to reach it Go Back can
 * still land on the tour's welcome modal. Accepted: it takes a two-entry history to get there, the
 * modal's own action advances back out, and the alternative is a Go Back button that silently does
 * nothing.
 */
export const goBackDistance = (tourId: string, stepHistory: string[] = []): number => {
  let index = walkBack(tourId, stepHistory, isLanding);
  if (!isLanding(getStep(tourId, stepHistory[index]))) {
    index = walkBack(tourId, stepHistory, isReenterable);
  }
  if (index < 0) return 0;
  return stepHistory.length - 1 - index;
};

const variantsOf = (step?: TourStep): BannerVariant[] => {
  const banner = step?.ui.banner;
  if (!banner) return [];
  return Array.isArray(banner) ? banner : [banner];
};

const guideVariantOf = (step?: TourStep): GuideBannerConfig | undefined =>
  variantsOf(step).find((v): v is GuideBannerConfig => 'type' in v && v.type === 'guide');

/**
 * The page a step is authored for, or undefined if it has none (a catch-all such as a paused
 * step renders anywhere). Taken from the guide variant's `when`, which lists the production path
 * first and the dev fixture second — every entry in the journey config is a literal path, so
 * this is a real URL rather than a pattern.
 */
export const stepHomePath = (step: TourStep | undefined, isDev: boolean): string | undefined => {
  const { when } = guideVariantOf(step) ?? {};
  if (!when) return undefined;
  const paths = Array.isArray(when) ? when : [when];
  return isDev ? paths[1] ?? paths[0] : paths[0];
};

/**
 * Where Go Back must navigate before landing on `step`, or undefined to stay put.
 *
 * A guide banner only renders on the page it was authored for; anywhere else the step falls
 * through to its paused catch-all. Going back is the one transition with no authored path of its
 * own — every other edge carries a button with `navigatePath` — so it has to work this out. A
 * section's first step is reached from the previous section's page, which is why nearly every
 * back button in the journey crosses a page boundary.
 */
export const backNavigationPath = (step: TourStep | undefined, pathname: string, isDev: boolean): string | undefined => {
  if (!step) return undefined;
  // Already somewhere the step renders properly: keep the current URL, query string and all.
  if (resolveUi(step, pathname).banner?.type === 'guide') return undefined;
  const home = stepHomePath(step, isDev);
  return home === pathname ? undefined : home;
};
